import { useAtomValue } from 'jotai';

import { userInventoryAtom } from 'src/stores/auth/atoms';
import {
  INVENTORY_ITEM_CATEGORY_ID_TYPES,
  type InventoryItemCategoryId,
} from 'src/types/itemInventory';
import { canUseInventoryItem } from 'src/utils/itemInventory';

const WEB_SUBSCRIPTION_ITEM_SOURCE = 'WEB_SUBSCRIPTION';

const SUBSCRIPTION_CATEGORY_IDS: InventoryItemCategoryId[] = [
  INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE,
  INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX,
];

export interface SubscriptionStatus {
  /** 앱(iOS/Android)에서 구독 중인지 여부 */
  hasAppSubscription: boolean;
  /** 웹에서 Core 구독 중인지 여부 */
  hasWebCoreSubscription: boolean;
  /** 웹에서 Max 구독 중인지 여부 */
  hasWebMaxSubscription: boolean;
  /** 중복 구독 중인지 여부 */
  hasDuplicateSubscription: boolean;
  /** 웹 Core 구독이 해지 예정(renew off)인지 여부. 구독이 없으면 null */
  isWebCoreRenewOff: boolean | null;
  /** 웹 Max 구독이 해지 예정(renew off)인지 여부. 구독이 없으면 null */
  isWebMaxRenewOff: boolean | null;
}

export const useSubscriptionStatus = (): SubscriptionStatus => {
  const inventoryItems = useAtomValue(userInventoryAtom);

  const validSubscriptions = inventoryItems?.filter(canUseInventoryItem) ?? [];

  const hasAppSubscription = validSubscriptions.some(
    (item) =>
      (item.categoryId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE ||
        item.categoryId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX) &&
      item.itemSource !== WEB_SUBSCRIPTION_ITEM_SOURCE
  );

  const webCoreSubscription = validSubscriptions.find(
    (item) =>
      item.categoryId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE &&
      item.itemSource === WEB_SUBSCRIPTION_ITEM_SOURCE
  );

  const webMaxSubscription = validSubscriptions.find(
    (item) =>
      item.categoryId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX &&
      item.itemSource === WEB_SUBSCRIPTION_ITEM_SOURCE
  );

  const totalSubscriptionCount = validSubscriptions.reduce(
    (acc, item) =>
      SUBSCRIPTION_CATEGORY_IDS.includes(item.categoryId)
        ? acc + (item.groupedItems?.length ?? 1)
        : acc,
    0
  );

  const hasDuplicateSubscription = totalSubscriptionCount > 1;

  return {
    hasAppSubscription,
    hasWebCoreSubscription: !!webCoreSubscription,
    hasWebMaxSubscription: !!webMaxSubscription,
    hasDuplicateSubscription,
    isWebCoreRenewOff: webCoreSubscription ? webCoreSubscription.autoRenewing === false : null,
    isWebMaxRenewOff: webMaxSubscription ? webMaxSubscription.autoRenewing === false : null,
  };
};
